import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import DataTable from '../../components/DataTable';
import type { Column } from '../../components/DataTable';
import {
  fetchSeedAccounts,
  createSeedAccounts,
  createSingleSeedAccount,
  updateSeedAccount,
  deleteSeedAccount,
  batchDeleteSeeds,
  createSeedVideos,
  toggleSeedAccounts,
  fetchCampaigns,
  type SeedAccount,
  type SeedCampaign,
} from '../../lib/seedService';
import { sendBulkNotification } from '../../lib/notificationService';
import { logger } from '../../lib/logger';

const SeedAccountsPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialCampaign = searchParams.get('campaign') || '';

  const [accounts, setAccounts] = useState<SeedAccount[]>([]);
  const [campaigns, setCampaigns] = useState<SeedCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [campaignFilter, setCampaignFilter] = useState(initialCampaign);
  const [typeFilter, setTypeFilter] = useState<string>('all');

  // Batch create
  const [showBatchCreate, setShowBatchCreate] = useState(false);
  const [batchCampaign, setBatchCampaign] = useState(initialCampaign);
  const [batchType, setBatchType] = useState<'candidate' | 'employer'>('candidate');
  const [batchCount, setBatchCount] = useState(5);
  const [batchCity, setBatchCity] = useState('');
  const [batchWithVideo, setBatchWithVideo] = useState(true);
  const [creating, setCreating] = useState(false);

  // Manual single create
  const [showManualCreate, setShowManualCreate] = useState(false);
  const [manualForm, setManualForm] = useState({
    campaignTag: initialCampaign,
    accountType: 'candidate' as 'candidate' | 'employer',
    firstName: '',
    lastName: '',
    city: '',
    email: '',
    companyName: '',
  });

  // Edit modal
  const [editAccount, setEditAccount] = useState<SeedAccount | null>(null);
  const [editForm, setEditForm] = useState({ firstName: '', lastName: '', city: '', companyName: '' });

  // Multi-select
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [batchActing, setBatchActing] = useState(false);

  // Notification modal
  const [showNotif, setShowNotif] = useState(false);
  const [notifTitle, setNotifTitle] = useState('');
  const [notifBody, setNotifBody] = useState('');

  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    const results = await Promise.allSettled([
      fetchSeedAccounts(campaignFilter || undefined),
      fetchCampaigns(),
    ]);

    if (results[0].status === 'fulfilled') setAccounts(results[0].value);
    else logger.error('load_seed_accounts', 'SeedAccountsPage', { error: String(results[0].reason) });

    if (results[1].status === 'fulfilled') setCampaigns(results[1].value.filter((x: SeedCampaign) => x.status === 'active'));
    else logger.error('load_campaigns', 'SeedAccountsPage', { error: String(results[1].reason) });

    setLoading(false);
  };

  useEffect(() => { load(); }, [campaignFilter]);

  const filtered = useMemo(() => {
    return accounts.filter((a) => {
      const matchSearch =
        search === '' ||
        `${a.firstName} ${a.lastName}`.toLowerCase().includes(search.toLowerCase()) ||
        a.email.toLowerCase().includes(search.toLowerCase()) ||
        a.city.toLowerCase().includes(search.toLowerCase());
      const matchType = typeFilter === 'all' || a.type === typeFilter;
      return matchSearch && matchType;
    });
  }, [accounts, search, typeFilter]);

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filtered.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map((a) => a.userId)));
    }
  };

  const handleBatchCreate = async () => {
    if (!batchCampaign) return;
    setCreating(true);
    try {
      const result = await createSeedAccounts({
        campaignTag: batchCampaign,
        accountType: batchType,
        count: batchCount,
        city: batchCity || undefined,
        withVideo: batchWithVideo && batchType === 'candidate',
      });
      setShowBatchCreate(false);
      await new Promise(r => setTimeout(r, 1500));
      await load();
      alert(`${result.created} comptes créés avec succès.`);
    } catch (err) {
      alert(`Erreur: ${(err as any)?.message || err}`);
    } finally {
      setCreating(false);
    }
  };

  const handleManualCreate = async () => {
    if (!manualForm.campaignTag || !manualForm.firstName || !manualForm.lastName) return;
    setCreating(true);
    try {
      await createSingleSeedAccount(manualForm);
      setShowManualCreate(false);
      setManualForm({ ...manualForm, firstName: '', lastName: '', city: '', email: '', companyName: '' });
      await new Promise(r => setTimeout(r, 1500));
      await load();
      alert('Compte créé avec succès.');
    } catch (err) {
      alert(`Erreur: ${(err as any)?.message || err}`);
    } finally {
      setCreating(false);
    }
  };

  const handleEdit = async () => {
    if (!editAccount) return;
    setActionLoading(editAccount.userId);
    try {
      await updateSeedAccount(editAccount.userId, {
        firstName: editForm.firstName,
        lastName: editForm.lastName,
        city: editForm.city,
        companyName: editForm.companyName,
        type: editAccount.type,
      });
      setEditAccount(null);
      load();
    } catch (err) {
      alert(`Erreur: ${(err as any)?.message || err}`);
    } finally {
      setActionLoading(null);
    }
  };

  const openEdit = (a: SeedAccount) => {
    setEditAccount(a);
    setEditForm({
      firstName: a.firstName,
      lastName: a.lastName,
      city: a.city,
      companyName: a.companyName || '',
    });
  };

  const handleDisable = async (a: SeedAccount) => {
    if (!confirm(`Supprimer le compte ${a.firstName} ${a.lastName} ?\n(Pour une coupure temporaire, utilisez plutôt « Désactiver ».)`)) return;
    setActionLoading(a.userId);
    try {
      await deleteSeedAccount(a.userId);
      load();
    } catch (err) {
      alert(`Erreur: ${(err as any)?.message || err}`);
    } finally {
      setActionLoading(null);
    }
  };

  /** Bascule réversible d'un compte seed (gel/dégel + contenu seed associé). */
  const handleToggleOne = async (a: SeedAccount) => {
    const action = a.status === 'suspended' ? 'reactivate' : 'deactivate';
    setActionLoading(a.userId);
    try {
      const r = await toggleSeedAccounts([a.userId], action);
      alert(`${action === 'deactivate' ? 'Désactivé' : 'Réactivé'} : ${r.offers} offre(s), ${r.videos} vidéo(s) impactée(s).`);
      load();
    } catch (err) {
      alert(`Erreur: ${(err as any)?.message || err}`);
    } finally {
      setActionLoading(null);
    }
  };

  /** Bascule réversible de la sélection. */
  const handleBatchToggle = async (action: 'deactivate' | 'reactivate') => {
    if (selectedIds.size === 0) return;
    if (!confirm(`${action === 'deactivate' ? 'Désactiver' : 'Réactiver'} ${selectedIds.size} compte(s) seed (réversible) ?`)) return;
    setBatchActing(true);
    try {
      const r = await toggleSeedAccounts(Array.from(selectedIds), action);
      alert(`${r.accounts} compte(s) ${action === 'deactivate' ? 'désactivé(s)' : 'réactivé(s)'} — ${r.offers} offre(s), ${r.videos} vidéo(s).`);
      setSelectedIds(new Set());
      load();
    } catch (err) {
      alert(`Erreur: ${(err as any)?.message || err}`);
    } finally {
      setBatchActing(false);
    }
  };

  const handleBatchDelete = async () => {
    if (!confirm(`Supprimer ${selectedIds.size} comptes seeds et TOUTES leurs traces ?`)) return;
    setBatchActing(true);
    try {
      const result = await batchDeleteSeeds(Array.from(selectedIds));
      alert(`${result.deleted} comptes supprimés.`);
      setSelectedIds(new Set());
      load();
    } catch (err) {
      alert(`Erreur: ${(err as any)?.message || err}`);
    } finally {
      setBatchActing(false);
    }
  };

  const handleBatchVideos = async () => {
    const candidateAccounts = accounts.filter(
      (a) => selectedIds.has(a.userId) && a.type === 'candidate' && (a.videoCount || 0) === 0
    );
    if (candidateAccounts.length === 0) {
      alert('Aucun candidat seed sans vidéo dans la sélection.');
      return;
    }
    setBatchActing(true);
    try {
      const { data: cpData } = await (await import('../../lib/supabase')).supabase
        .from('candidate_profiles')
        .select('id, user_id')
        .in('user_id', candidateAccounts.map((a) => a.userId));

      const cpIds = (cpData || []).map((c: any) => c.id);
      const tag = candidateAccounts[0].seedTag;
      const count = await (await import('../../lib/seedService')).createSeedVideos({
        campaignTag: tag,
        candidateProfileIds: cpIds,
      });
      alert(`${count} vidéos seed créées.`);
      load();
    } catch (err) {
      alert(`Erreur: ${(err as any)?.message || err}`);
    } finally {
      setBatchActing(false);
    }
  };

  const handleBatchNotif = async () => {
    if (!notifTitle || !notifBody) return;
    setBatchActing(true);
    try {
      const count = await sendBulkNotification(Array.from(selectedIds), notifTitle, notifBody);
      alert(`${count} notifications envoyées.`);
      setShowNotif(false);
      setNotifTitle('');
      setNotifBody('');
    } catch (err) {
      alert(`Erreur: ${(err as any)?.message || err}`);
    } finally {
      setBatchActing(false);
    }
  };

  const columns: Column<SeedAccount>[] = [
    {
      key: 'select',
      header: '',
      width: '40px',
      render: (a) => (
        <input
          type="checkbox"
          checked={selectedIds.has(a.userId)}
          onChange={() => toggleSelect(a.userId)}
          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
        />
      ),
    },
    {
      key: 'avatar',
      header: '',
      width: '44px',
      render: (a) => (
        <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold ${a.type === 'candidate' ? 'bg-indigo-500' : 'bg-purple-500'}`}>
          {a.firstName?.[0]}{a.lastName?.[0]}
        </div>
      ),
    },
    {
      key: 'name',
      header: 'Nom',
      render: (a) => (
        <div>
          <div className="flex items-center gap-2">
            <button onClick={() => openEdit(a)} className="font-medium text-gray-900 hover:text-indigo-600 transition-colors">
              {a.firstName} {a.lastName}
            </button>
            <span className="px-1.5 py-0.5 text-[9px] font-bold rounded bg-indigo-100 text-indigo-700 uppercase">seed</span>
          </div>
          <p className="text-xs text-gray-400">{a.email}</p>
        </div>
      ),
    },
    {
      key: 'type',
      header: 'Type',
      render: (a) => (
        <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-md ${a.type === 'candidate' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'}`}>
          {a.type === 'candidate' ? 'Candidat' : 'Recruteur'}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'État',
      render: (a) => (
        <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-md ${a.status === 'suspended' ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'}`}>
          {a.status === 'suspended' ? 'Désactivé' : 'Actif'}
        </span>
      ),
    },
    {
      key: 'campaign',
      header: 'Campagne',
      render: (a) => <span className="text-xs text-gray-600 font-mono">{a.seedTag}</span>,
    },
    {
      key: 'city',
      header: 'Ville',
      render: (a) => <span className="text-gray-600">{a.city || '—'}</span>,
    },
    {
      key: 'videos',
      header: 'Vidéos',
      render: (a) => (
        <span className={`text-xs font-medium ${(a.videoCount || 0) > 0 ? 'text-emerald-600' : 'text-gray-400'}`}>
          {a.type === 'candidate' ? (a.videoCount || 0) : '—'}
        </span>
      ),
    },
    {
      key: 'createdAt',
      header: 'Créé le',
      render: (a) => <span className="text-gray-500 text-xs">{a.createdAt}</span>,
    },
    {
      key: 'actions',
      header: '',
      render: (a) => (
        <div className="flex items-center gap-1">
          <button
            onClick={() => navigate(`/command-board/traces/${a.userId}`)}
            className="px-2 py-1 text-xs font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-md transition-colors"
          >
            Traces
          </button>
          <button
            onClick={() => openEdit(a)}
            className="px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
          >
            Éditer
          </button>
          <button
            onClick={() => handleToggleOne(a)}
            disabled={actionLoading === a.userId}
            className={`px-2 py-1 text-xs font-medium rounded-md transition-colors disabled:opacity-40 ${a.status === 'suspended' ? 'text-emerald-700 bg-emerald-50 hover:bg-emerald-100' : 'text-amber-700 bg-amber-50 hover:bg-amber-100'}`}
            title={a.status === 'suspended' ? 'Réactive le compte et son contenu seed' : 'Gèle le compte, ferme ses offres, retire ses vidéos du feed. Réversible.'}
          >
            {a.status === 'suspended' ? 'Réactiver' : 'Désactiver'}
          </button>
          <button
            onClick={() => handleDisable(a)}
            disabled={actionLoading === a.userId}
            className="px-2 py-1 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-md transition-colors disabled:opacity-40"
          >
            Supprimer
          </button>
        </div>
      ),
    },
  ];

  const filterSlot = (
    <div className="flex items-center gap-2">
      <select
        value={campaignFilter}
        onChange={(e) => setCampaignFilter(e.target.value)}
        className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
      >
        <option value="">Toutes les campagnes</option>
        {campaigns.map((c) => (
          <option key={c.id} value={c.name}>{c.name}</option>
        ))}
      </select>
      <select
        value={typeFilter}
        onChange={(e) => setTypeFilter(e.target.value)}
        className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
      >
        <option value="all">Tous les types</option>
        <option value="candidate">Candidats</option>
        <option value="employer">Recruteurs</option>
      </select>
      <button
        onClick={toggleSelectAll}
        className="px-3 py-2 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
      >
        {selectedIds.size === filtered.length && filtered.length > 0 ? 'Tout désélectionner' : 'Tout sélectionner'}
      </button>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-indigo-300 border-t-indigo-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* KPIs */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="bg-white rounded-xl border border-indigo-200 px-4 py-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold">#</div>
            <div>
              <p className="text-xl font-bold text-indigo-600">{accounts.length}</p>
              <p className="text-xs text-gray-500">Comptes seeds</p>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-blue-200 px-4 py-3">
            <p className="text-xl font-bold text-blue-600">{accounts.filter((a) => a.type === 'candidate').length}</p>
            <p className="text-xs text-gray-500">Candidats</p>
          </div>
          <div className="bg-white rounded-xl border border-purple-200 px-4 py-3">
            <p className="text-xl font-bold text-purple-600">{accounts.filter((a) => a.type === 'employer').length}</p>
            <p className="text-xs text-gray-500">Recruteurs</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowManualCreate(true)}
            className="px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded-lg transition-colors"
          >
            + Créer manuellement
          </button>
          <button
            onClick={() => setShowBatchCreate(true)}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors"
          >
            + Création rapide
          </button>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={filtered}
        keyExtractor={(a) => a.userId}
        searchable
        searchPlaceholder="Rechercher par nom, email, ville..."
        onSearch={setSearch}
        filterSlot={filterSlot}
        pageSize={15}
      />

      {/* Batch action bar */}
      {selectedIds.size > 0 && (
        <div className="fixed bottom-0 left-[250px] right-0 bg-white border-t border-gray-200 shadow-lg px-6 py-3 flex items-center justify-between z-40">
          <p className="text-sm font-medium text-gray-700">
            {selectedIds.size} compte(s) sélectionné(s)
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={handleBatchVideos}
              disabled={batchActing}
              className="px-4 py-2 text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors disabled:opacity-40"
            >
              Générer vidéos
            </button>
            <button
              onClick={() => setShowNotif(true)}
              disabled={batchActing}
              className="px-4 py-2 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors disabled:opacity-40"
            >
              Envoyer notification
            </button>
            <button
              onClick={() => handleBatchToggle('deactivate')}
              disabled={batchActing}
              className="px-4 py-2 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors disabled:opacity-40"
              title="Gèle les comptes, ferme leurs offres, retire leurs vidéos du feed. Réversible."
            >
              Désactiver
            </button>
            <button
              onClick={() => handleBatchToggle('reactivate')}
              disabled={batchActing}
              className="px-4 py-2 text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors disabled:opacity-40"
            >
              Réactiver
            </button>
            <button
              onClick={handleBatchDelete}
              disabled={batchActing}
              className="px-4 py-2 text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded-lg transition-colors disabled:opacity-40"
            >
              Supprimer {selectedIds.size} comptes + traces
            </button>
          </div>
        </div>
      )}

      {/* Batch create modal */}
      {showBatchCreate && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setShowBatchCreate(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-indigo-50 to-purple-50">
              <h3 className="text-lg font-semibold text-gray-900">Création rapide (batch)</h3>
              <p className="text-xs text-gray-500 mt-1">Génère des comptes seeds avec des données aléatoires</p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Campagne</label>
                <select value={batchCampaign} onChange={(e) => setBatchCampaign(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200">
                  <option value="">Sélectionner...</option>
                  {campaigns.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select value={batchType} onChange={(e) => setBatchType(e.target.value as any)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200">
                    <option value="candidate">Candidat</option>
                    <option value="employer">Recruteur</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nombre (1-50)</label>
                  <input type="number" min={1} max={50} value={batchCount} onChange={(e) => setBatchCount(Math.min(50, Math.max(1, Number(e.target.value))))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ville (optionnel)</label>
                <input type="text" value={batchCity} onChange={(e) => setBatchCity(e.target.value)} placeholder="Laisser vide pour aléatoire" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200" />
              </div>
              {batchType === 'candidate' && (
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input type="checkbox" checked={batchWithVideo} onChange={(e) => setBatchWithVideo(e.target.checked)} className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                  Générer une vidéo placeholder pour chaque compte
                </label>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3 bg-gray-50">
              <button onClick={() => setShowBatchCreate(false)} className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors">Annuler</button>
              <button onClick={handleBatchCreate} disabled={!batchCampaign || creating} className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors disabled:opacity-40">
                {creating ? 'Création en cours...' : `Créer ${batchCount} comptes`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manual create modal */}
      {showManualCreate && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setShowManualCreate(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-emerald-50 to-teal-50">
              <h3 className="text-lg font-semibold text-gray-900">Création manuelle</h3>
              <p className="text-xs text-gray-500 mt-1">Créer un compte seed avec des données personnalisées</p>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Campagne</label>
                  <select value={manualForm.campaignTag} onChange={(e) => setManualForm({ ...manualForm, campaignTag: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200">
                    <option value="">Sélectionner...</option>
                    {campaigns.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select value={manualForm.accountType} onChange={(e) => setManualForm({ ...manualForm, accountType: e.target.value as any })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200">
                    <option value="candidate">Candidat</option>
                    <option value="employer">Recruteur</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prénom</label>
                  <input type="text" value={manualForm.firstName} onChange={(e) => setManualForm({ ...manualForm, firstName: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200" autoFocus />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                  <input type="text" value={manualForm.lastName} onChange={(e) => setManualForm({ ...manualForm, lastName: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ville</label>
                  <input type="text" value={manualForm.city} onChange={(e) => setManualForm({ ...manualForm, city: e.target.value })} placeholder="Paris" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email (optionnel)</label>
                  <input type="email" value={manualForm.email} onChange={(e) => setManualForm({ ...manualForm, email: e.target.value })} placeholder="Auto-généré si vide" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200" />
                </div>
              </div>
              {manualForm.accountType === 'employer' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom de l'entreprise</label>
                  <input type="text" value={manualForm.companyName} onChange={(e) => setManualForm({ ...manualForm, companyName: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200" />
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3 bg-gray-50">
              <button onClick={() => setShowManualCreate(false)} className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors">Annuler</button>
              <button onClick={handleManualCreate} disabled={!manualForm.campaignTag || !manualForm.firstName || !manualForm.lastName || creating} className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors disabled:opacity-40">
                {creating ? 'Création...' : 'Créer le compte'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editAccount && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setEditAccount(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-amber-50 to-orange-50">
              <h3 className="text-lg font-semibold text-gray-900">Modifier le compte seed</h3>
              <p className="text-xs text-gray-400 mt-1">{editAccount.email}</p>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prénom</label>
                  <input type="text" value={editForm.firstName} onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-200" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                  <input type="text" value={editForm.lastName} onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-200" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ville</label>
                <input type="text" value={editForm.city} onChange={(e) => setEditForm({ ...editForm, city: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-200" />
              </div>
              {editAccount.type === 'employer' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom entreprise</label>
                  <input type="text" value={editForm.companyName} onChange={(e) => setEditForm({ ...editForm, companyName: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-200" />
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3 bg-gray-50">
              <button onClick={() => setEditAccount(null)} className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors">Annuler</button>
              <button onClick={handleEdit} disabled={actionLoading === editAccount.userId} className="px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg transition-colors disabled:opacity-40">
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification modal */}
      {showNotif && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setShowNotif(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-cyan-50">
              <h3 className="text-lg font-semibold text-gray-900">Envoyer une notification</h3>
              <p className="text-xs text-gray-500 mt-1">À {selectedIds.size} compte(s) sélectionné(s)</p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titre</label>
                <input type="text" value={notifTitle} onChange={(e) => setNotifTitle(e.target.value)} placeholder="Ex: Bienvenue sur Power Jobs !" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-200" autoFocus />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                <textarea value={notifBody} onChange={(e) => setNotifBody(e.target.value)} rows={3} placeholder="Contenu de la notification..." className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 resize-none" />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3 bg-gray-50">
              <button onClick={() => setShowNotif(false)} className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors">Annuler</button>
              <button onClick={handleBatchNotif} disabled={!notifTitle || !notifBody || batchActing} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-40">
                {batchActing ? 'Envoi...' : 'Envoyer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SeedAccountsPage;
